import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormikProps, FieldProps, FastFieldProps } from 'formik';

import FormControl, { FormControlProps } from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormLabel from '@material-ui/core/FormLabel';
import Radio from '@material-ui/core/Radio';
import FormHelperText from '@material-ui/core/FormHelperText';
import RadioGroup, { RadioGroupProps } from '@material-ui/core/RadioGroup';
import Box from '@material-ui/core/Box';
import { Theme } from '@material-ui/core/styles/createTheme';
import makeStyles from '@material-ui/core/styles/makeStyles';

import { getErrorMessageKey } from 'helpers/helpers';
import { withField } from 'components/FormFields/withFields';
import Info from 'components/Tooltip/Info';
import { Extension } from 'pages/Accounts/Edit/Investments/questionnaireStructure';

const styles = (theme: Theme) => ({
  formGroup: (props: any) => ({
    marginBottom: props.noMargin ? undefined : theme.spacing(4),
    '&:last-child:not(:only-child)': {
      marginBottom: 0,
    },
    '& ._withImage .MuiButtonBase-root': {
      left: '100px',
    },
  }),
  extended: {
    borderRadius: '6px',
    boxShadow: '0 0 10px 1px rgba(0,0,0,0.13)',
    padding: theme.spacing(2),
    margin: theme.spacing(1, 0),
    '&:first-child': { marginTop: theme.spacing(2) },
    '& .MuiFormControlLabel-label': {
      marginLeft: theme.spacing(4),
      display: 'flex',
      alignItems: 'center',

      '& > span': {
        display: 'flex',
        alignItems: 'center',
        '& span, & em': { fontWeight: 500, display: 'inline-flex' },
        '& span': { minWidth: '100px' },
        '& em': { fontStyle: 'normal', fontSize: '0.9rem' },
      },
    },
    '& .MuiRadio-root': { transform: `translate(${theme.spacing(8)}px,0)` },
    '& img': { transform: `translate(-${theme.spacing(8)}px,0)` },
  },
  info: {
    //padding: theme.spacing(2, 0),
    fontSize: '14px',
    fontWeight: 400,
    color: '#58585a',
  },
  font: {
    '& .MuiFormLabel-root, & .MuiTypography-root': { fontSize: '14px' },
    '& .MuiSvgIcon-root': { fontSize: '22px' },
  },
});

export interface RadioGroupValue {
  value: string;
  label: string;
  tooltip?: string;
  control?: React.ReactElement;
}

interface OwnProps {
  id?: string;
  formikProps?: FormikProps<any>;
  values: RadioGroupValue[];
  label: string;
  inline?: boolean;
  images?: string[];
  specificError?: boolean;
  disabledAnswers?: string[];
  escapeValues?: boolean;
  noMargin?: boolean;
  fast?: boolean;
  extended?: Extension[];
  tooltip?: string;
  info?: string;
  light?: boolean;
}

type Props = OwnProps & RadioGroupProps & FormControlProps & (FieldProps | FastFieldProps);

const CustomFormRadioGroup: React.FC<Props> = ({
  id,
  label,
  values,
  fullWidth,
  className,
  field,
  meta,
  inline,
  images,
  specificError,
  disabledAnswers,
  escapeValues,
  noMargin,
  fast,
  extended,
  tooltip,
  info,
}) => {
  const { t } = useTranslation();
  const newId = id || field.name;
  const errorMessage = meta.error ? t(getErrorMessageKey(meta.error, specificError)) : '';
  const useStyles = makeStyles(styles);
  const classes = useStyles({ noMargin });

  return (
    <FormControl
      fullWidth={fullWidth || true}
      className={`${classes.formGroup} ${classes.font} ${className}`}
      error={!!meta.error}
    >
      <FormLabel style={{ marginBottom: '8px' }}>
        <strong>{label}</strong>
        {tooltip && (
          <Info
            content={
              <span
                dangerouslySetInnerHTML={{
                  __html: tooltip,
                }}
              />
            }
            inline
          />
        )}
        {info && <p style={{ color: '#58585a' }}>{info}</p>}
      </FormLabel>
      <RadioGroup
        {...field}
        id={newId}
        style={{
          display: 'flex',
          flexDirection: inline || images ? 'row' : 'column',
          alignItems: extended ? undefined : 'flex-start',
        }}
      >
        {values.map(({ value: controlValue, control, label: controlLabel, tooltip }, i) => (
          <ControlLabel
            controlValue={controlValue}
            control={control}
            controlLabel={controlLabel}
            key={controlValue}
            image={images ? images[i] : undefined}
            disabled={disabledAnswers && disabledAnswers.indexOf(controlValue) > -1}
            escapeValues={escapeValues}
            tooltip={tooltip}
            extension={extended ? extended[i] : undefined}
            checked={field.value === controlValue}
          />
        ))}
      </RadioGroup>
      {errorMessage && <FormHelperText style={{ color: 'red' }}>{errorMessage}</FormHelperText>}
    </FormControl>
  );
};

interface ControlProps {
  controlValue: string;
  control: React.ReactElement | undefined;
  controlLabel: string;
  image?: string;
  disabled?: boolean;
  escapeValues?: boolean;
  tooltip?: string;
  info?: string;
  extension?: Extension;
  checked?: boolean;
}

const ControlLabel: React.FC<ControlProps> = React.memo(
  ({ controlValue, control, controlLabel, image, disabled, escapeValues, tooltip, info, extension, checked }) => {
    const useStyles = makeStyles(styles);
    const classes = useStyles({});
    const labelText =
      escapeValues === false ? (
        <span
          dangerouslySetInnerHTML={{
            __html: controlLabel,
          }}
        />
      ) : (
        controlLabel
      );

    if (extension)
      return (
        <div className={classes.extended} style={{ background: checked ? extension.activeColor : undefined }}>
          <FormControlLabel
            value={controlValue}
            control={control || <Radio />}
            disabled={disabled}
            label={
              <>
                <img width="56" height="56" alt={controlLabel} src={extension.icon} style={{ marginRight: '16px' }} />
                {labelText}
              </>
            }
          />
        </div>
      );

    return image ? (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        mr={4}
        mt={1}
        style={{ marginRight: 0 }}
        className="_withImage"
      >
        <FormControlLabel
          disabled={disabled}
          value={controlValue}
          control={control || <Radio />}
          style={{ alignItems: 'flex-end', marginRight: '24px', textAlign: 'center' }}
          label={
            <Box display="flex" flexDirection="column" alignItems="flex-start" pb={1}>
              <img width="300" height="200" alt={controlLabel} src={image} style={{ marginLeft: -30 }} />
              <Box component="span" mt={1} style={{ paddingLeft: '100px' }}>
                {labelText}
              </Box>
            </Box>
          }
        />
      </Box>
    ) : (
      <FormControlLabel
        value={controlValue}
        control={control || <Radio />}
        disabled={disabled}
        label={
          tooltip ? (
            <>
              {labelText}
              <Info inline content={<>{tooltip}</>} />
            </>
          ) : (
            labelText
          )
        }
      />
    );
  },
);

const FormRadioGroup = withField(CustomFormRadioGroup);

export default FormRadioGroup;
